import {Content, Root, Portal, Close} from '@radix-ui/react-dialog'
import {AnimatePresence, m as motion} from 'framer-motion'
import React from 'react'
import styled from 'styled-components'

import type {AspectRatio} from '@/components/inline-wistia-video'
import InlineWistiaVideo from '@/components/inline-wistia-video'
import {text} from '@/components/text'
import {VisuallyHidden} from '@/components/visually-hidden'
import closeIcon from '@/images/icons/close-white.svg'
import {useGlobalStrings} from '@/providers/global-strings'
import {zIndex, breakpoints, tokens} from '@/theme'
import mq from '@/utils/media-query'

interface Props {
    videoId: string
    children: (props: {openPopover: () => void}) => React.ReactNode
    aspectRatio?: AspectRatio
}

const PopoverVideo = ({children, videoId, aspectRatio}: Props) => {
    const [showDialog, setShowDialog] = React.useState(false)
    const open = () => setShowDialog(true)
    const close = () => setShowDialog(false)
    const {close_button_text} = useGlobalStrings()

    return (
        <>
            <AnimatePresence>
                {showDialog && (
                    <Root open={showDialog} onOpenChange={(open) => !open && close()}>
                        <Portal>
                            <VideoPopoverOverlay {...overlayAnimation}>
                                <motion.div {...contentAnimation}>
                                    <VideoPopoverContent aria-label="Video">
                                        <motion.div {...closeButtonAnimation}>
                                            <ClosePopover>
                                                <VisuallyHidden>{close_button_text}</VisuallyHidden>
                                                <span aria-hidden>
                                                    <img alt="" src={closeIcon} />
                                                </span>
                                            </ClosePopover>
                                        </motion.div>
                                        <InlineWistiaVideo
                                            videoId={videoId}
                                            autoPlay
                                            aspectRatio={aspectRatio}
                                            maxHeight={window.innerHeight * 0.8}
                                        />
                                    </VideoPopoverContent>
                                </motion.div>
                            </VideoPopoverOverlay>
                        </Portal>
                    </Root>
                )}
            </AnimatePresence>
            {typeof children === 'function' &&
                children({
                    openPopover: open,
                })}
        </>
    )
}

const VideoPopoverOverlay = styled(motion.create('div'))`
    pointer-events: none;
    position: fixed;
    z-index: ${zIndex.videoPopover};
    overflow: auto;
    background: ${tokens.shade900lighten80};
    inset: 0;
`

const VideoPopoverContent = styled(Content)`
    position: relative;
    width: 65vw;
    max-width: ${breakpoints.lg}px;
    margin: 10vh auto;
    outline: none;

    ${mq.max('md')} {
        width: 100%;
    }
`

const ClosePopover = styled(Close)`
    position: absolute;
    top: -15px;
    right: -${tokens.spacing40};
    cursor: pointer;
    background: none;
    border: none;
    ${text({$color: 'inverted', $variant: 'title-base'})}

    ${mq.max('md')} {
        top: -${tokens.spacing40};
        right: 0;
    }
`

const overlayAnimation = {
    initial: {opacity: 0},
    animate: {opacity: 1},
    exit: {opacity: 0},
}

const contentAnimation = {
    initial: {y: '-40px', opacity: 0},
    animate: {y: '0', opacity: 1},
    exit: {
        y: '40px',
        opacity: 0,
        transition: {delay: 0},
    },
    transition: {y: {type: 'spring', stiffness: 100}, delay: 0.8},
}

const closeButtonAnimation = {
    initial: {x: '-30px', opacity: 0},
    animate: {x: '0', opacity: 1},
    transition: {y: {type: 'spring', stiffness: 100}, delay: 1},
}

export default PopoverVideo
